import React, { useState, useMemo, useEffect } from 'react';
import { toast } from 'sonner';
import { Check } from 'lucide-react';
import { PxWindow, ChipLogo } from '../components/PixelUI';
import MyRentals from '../components/MyRentals';
import { Slider } from '../components/ui/slider';
import { getQuote, createRental, getRentalStatus, getDiesAvailability } from '../api';
import { devWarn } from '../lib/log';
import { DIES, PAYMENT_ADDRESS, fmtUsd } from '../catalog';

const ETHERSCAN_TX = 'https://etherscan.io/tx/';

const STATUS_TONE = {
  PROVISIONING: 'var(--nv-green)',
  PAID_QUEUED: 'var(--nv-gold)',
  PAYMENT_PENDING: '#8a6a10',
  PAYMENT_UNVERIFIED: '#ff6b6b',
  PAID_PROVISION_FAILED: '#ff6b6b',
};

const usdOf = (eth, quote) => {
  if (quote === null || quote === undefined) return null;
  return eth * quote;
};

const UsdLeg = ({ eth, quote }) => {
  const v = usdOf(eth, quote);
  if (v === null) return <span style={{ fontStyle: 'italic', opacity: 0.55 }}>no signal</span>;
  return <>≈ {fmtUsd(v)}</>;
};

/* ---------------- die card ---------------- */
const STOCK_LABEL = {
  AVAILABLE: (a) => `${(a.stock || 'in').toLowerCase()} stock`,
  SOLD_OUT: () => 'sold out',
  UNKNOWN: () => 'stock unknown',
};
const STOCK_TONE = { AVAILABLE: 'var(--nv-green)', SOLD_OUT: '#ff6b6b', UNKNOWN: 'var(--nv-brown)' };

const DieCard = ({ d, selected, quote, onSelect, avail }) => {
  const state = avail?.state;
  return (
  <PxWindow title={d.name} tape className={`nv-die-card ${selected ? 'selected' : ''}`}>
    <div className="body">
      <div className="nv-die-blurb"><ChipLogo size={26} /> {d.blurb}</div>
      <div className="nv-kv"><span className="k">VRAM</span><span className="v">{d.vram}</span></div>
      <div className="nv-kv"><span className="k">Fill rate</span><span className="v">{d.fill}</span></div>
      <div className="nv-kv"><span className="k">Palette lanes</span><span className="v">{d.lanes}</span></div>
      <div className="nv-kv"><span className="k">Real silicon</span><span className="v">{d.gpu}</span></div>
      <div className="nv-kv">
        <span className="k">Provider</span>
        <span className="v" style={{ color: state ? STOCK_TONE[state] : 'var(--nv-brown)', fontSize: 11, whiteSpace: 'nowrap' }} data-testid={`die-stock-${d.id}`}>
          {state ? STOCK_LABEL[state](avail) : 'checking…'}
        </span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 10 }}>
        <div className="nv-die-price">
          {d.ethPerHour} ETH
          <small>per hour · <UsdLeg eth={d.ethPerHour} quote={quote} /></small>
        </div>
        <button
          className={`px-btn sm ${selected ? '' : 'ghost'}`}
          onClick={() => onSelect(d.id)}
          data-testid={`select-die-${d.id}`}
        >
          {selected ? <><Check size={12} /> Selected</> : 'Select'}
        </button>
      </div>
    </div>
  </PxWindow>
  );
};

/* ---------------- job configurator ---------------- */
const JobConfig = ({ die, hours, setHours, totalEth, quote, paying, soldOut, onPay }) => {
  const sliderValue = useMemo(() => [hours], [hours]);
  return (
  <PxWindow title="JOB.CFG" tape>
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
        <span style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', fontWeight: 700, color: 'var(--nv-brown)' }}>Configure the job</span>
        <span className="font-pixel" style={{ fontSize: 10, background: 'var(--nv-gold)', border: '2px solid var(--nv-ink)', padding: '6px 10px' }}>{die.name}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
        <span style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--nv-brown)', fontWeight: 700 }}>Hours on the core</span>
        <span className="font-pixel" style={{ fontSize: 16 }} data-testid="hours-value">{hours}h</span>
      </div>
      <Slider value={sliderValue} min={1} max={720} step={1} onValueChange={(v) => setHours(v[0])} data-testid="hours-slider" />
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--nv-brown)', marginTop: 8, marginBottom: 22 }}>
        <span>1h</span><span>a weekend</span><span>a month</span>
      </div>
      <div style={{ borderTop: '2px dashed rgba(42,26,14,.35)' }}>
        <div className="nv-total-row">
          <span className="lab">Total</span>
          <span className="val" data-testid="total-eth">{totalEth.toFixed(4)} ETH <span style={{ fontFamily: 'Space Mono', fontSize: 12, color: 'var(--nv-brown)' }}><UsdLeg eth={totalEth} quote={quote} /></span></span>
        </div>
      </div>
      <button
        className="px-btn"
        style={{ width: '100%', marginTop: 16, ...(soldOut ? { background: '#d8cfb8', color: '#5b4a33' } : {}) }}
        onClick={onPay}
        disabled={paying || soldOut}
        data-testid="dock-and-pay-btn"
      >
        {soldOut ? 'Sold out — pick another core' : (paying ? 'Waiting on the wallet…' : 'Dock & Pay')}
      </button>
      <p style={{ fontSize: 10.5, color: 'var(--nv-brown)', marginTop: 12, lineHeight: 1.6, textAlign: 'center' }}>
        {soldOut
          ? 'this core has no capacity on the provider right now — try another or check back soon'
          : 'dock your wallet, sign the job, and the core spins up on the next sealed block'}
      </p>
    </div>
  </PxWindow>
  );
};

/* ---------------- receipt ---------------- */
const ReceiptWindow = ({ receipt }) => {
  const [live, setLive] = useState(receipt);

  useEffect(() => {
    setLive(receipt);
    let alive = true;
    let timer = null;
    const settled = (s) => ['PAYMENT_UNVERIFIED', 'PAID_PROVISION_FAILED', 'PAID_QUEUED'].includes(s);
    const poll = async () => {
      const next = await getRentalStatus(receipt.order_id);
      if (!alive) return;
      if (next) setLive(next);
      const stop = next && (next.pod_ready || settled(next.status));
      if (!stop) timer = setTimeout(poll, 8000);
    };
    poll();
    return () => { alive = false; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receipt.order_id]);

  const tone = STATUS_TONE[live.status] || 'var(--nv-cream)';
  const tx = live.tx || {};
  const jupyter = (live.pod_ports || []).find((p) => p.type === 'http');
  const ssh = (live.pod_ports || []).find((p) => p.type === 'tcp');

  return (
    <PxWindow title="RECEIPT.ROM" tape>
      <div style={{ padding: 18 }} data-testid="rental-receipt">
        <p className="font-pixel" style={{ fontSize: 10, color: tone, margin: '0 0 12px' }} data-testid="receipt-status-banner">
          {live.status === 'PAYMENT_PENDING'
            ? 'PAYMENT SENT · CONFIRMING ON-CHAIN'
            : (live.status === 'PROVISIONING' && !live.pod_ready ? 'JOB ACCEPTED · BOOTING' : 'JOB ACCEPTED')}
        </p>
        <div className="nv-kv"><span className="k">Order</span><span className="v" style={{ fontSize: 12 }}>{live.order_id}</span></div>
        <div className="nv-kv"><span className="k">Core</span><span className="v">{live.die_name} · {live.hours}h</span></div>

        <div className="nv-kv">
          <span className="k">Payment</span>
          <span className="v" style={{ color: tx.confirmed ? 'var(--nv-green)' : 'var(--nv-gold)' }} data-testid="receipt-tx-status">
            {tx.confirmed ? `confirmed · ${tx.confirmations} conf` : 'awaiting confirmation…'}
          </span>
        </div>
        {live.tx_hash && (
          <div className="nv-kv">
            <span className="k">Tx</span>
            <span className="v" style={{ fontSize: 12 }}>
              <a href={`${ETHERSCAN_TX}${live.tx_hash}`} target="_blank" rel="noreferrer" style={{ color: 'var(--nv-green)' }} data-testid="receipt-tx-link">
                {live.tx_hash.slice(0, 10)}…{live.tx_hash.slice(-6)} ↗
              </a>
            </span>
          </div>
        )}

        <div className="nv-kv">
          <span className="k">Die</span>
          <span className="v" style={{ color: tone }} data-testid="receipt-pod-status">
            {live.pod_ready ? 'running — ready' : (live.pod_desired_status || live.status)}
          </span>
        </div>

        {jupyter && (
          <div className="nv-kv"><span className="k">Jupyter</span><span className="v" style={{ fontSize: 12 }}>{jupyter.ip}:{jupyter.public}</span></div>
        )}
        {ssh && (
          <div className="nv-kv"><span className="k">SSH</span><span className="v" style={{ fontSize: 12 }}>{ssh.ip}:{ssh.public}</span></div>
        )}
        {live.connect && !live.pod_ready && (
          <div className="nv-kv"><span className="k">Connect</span><span className="v" style={{ fontSize: 12 }}>{live.connect}</span></div>
        )}
        <p style={{ fontSize: 11.5, color: 'var(--nv-brown)', marginTop: 10 }}>{live.note}</p>
      </div>
    </PxWindow>
  );
};

/* ---------------- spec sheet + readme ---------------- */
const SpecSheet = ({ quote }) => (
  <PxWindow title="SPEC.SHEET" tape>
    <div style={{ overflowX: 'auto', padding: 8 }}>
      <table className="nv-spec-table" data-testid="spec-table">
        <thead>
          <tr><th>Core</th><th>VRAM</th><th>Fill</th><th>ETH/h</th><th>USD/h</th></tr>
        </thead>
        <tbody>
          {DIES.map((d) => (
            <tr key={d.id}>
              <td style={{ fontWeight: 700 }}>{d.name}</td>
              <td>{d.vram}</td>
              <td>{d.fill}</td>
              <td>{d.ethPerHour}</td>
              <td><UsdLeg eth={d.ethPerHour} quote={quote} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </PxWindow>
);

const ReadMe = () => (
  <PxWindow title="READ.ME" tape>
    <div style={{ padding: 18, fontSize: 12.5, lineHeight: 1.8 }}>
      <p style={{ margin: 0 }}>
        · NV8 cores wear playful retro names, but each one maps to real iron
        (A4000 → A100 80GB) provisioned on demand once your wallet signs the job.
      </p>
      <p style={{ margin: '10px 0 0' }}>
        · No human ever types a dollar into this page — every USD figure is the core's ETH
        price multiplied by the ledger's live quote, refreshed each minute. If the quote
        can't be read, the column shows no signal.
      </p>
    </div>
  </PxWindow>
);

/* ---------------- payment flow ---------------- */
const ETH_MAINNET = '0x1';

const ensureMainnet = async () => {
  const chainId = await window.ethereum.request({ method: 'eth_chainId' });
  if (chainId === ETH_MAINNET) return;
  await window.ethereum.request({
    method: 'wallet_switchEthereumChain',
    params: [{ chainId: ETH_MAINNET }],
  });
};

const payWithWallet = async ({ die, hours, totalEth, quote }) => {
  const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
  await ensureMainnet();
  const weiHex = '0x' + BigInt(Math.round(totalEth * 1e18)).toString(16);
  const txHash = await window.ethereum.request({
    method: 'eth_sendTransaction',
    params: [{ from: accounts[0], to: PAYMENT_ADDRESS, value: weiHex }],
  });
  return createRental({
    die_id: die.id, die_name: die.name, hours,
    wallet: accounts[0], tx_hash: txHash, total_eth: totalEth,
    usd_quote: quote,
  });
};

/* ---------------- page ---------------- */
const Rent = () => {
  const [selected, setSelected] = useState(DIES[1].id);
  const [hours, setHours] = useState(24);
  const [paying, setPaying] = useState(false);
  const [quote, setQuote] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [availability, setAvailability] = useState(null);

  useEffect(() => {
    let alive = true;
    const pull = async () => {
      const q = await getQuote();
      if (alive) setQuote(q);
    };
    const pullAvail = async () => {
      const a = await getDiesAvailability();
      if (alive && a) setAvailability(a);
    };
    pull();
    pullAvail();
    const t = setInterval(pull, 60000);
    const ta = setInterval(pullAvail, 60000);
    return () => { alive = false; clearInterval(t); clearInterval(ta); };
  }, []);

  const die = useMemo(() => DIES.find((d) => d.id === selected), [selected]);
  const totalEth = useMemo(() => die.ethPerHour * hours, [die, hours]);
  const dieAvail = availability ? availability[selected] : null;
  const soldOut = dieAvail ? dieAvail.state === 'SOLD_OUT' : false;

  const pay = async () => {
    if (!window.ethereum) {
      toast.error('No wallet docked — install MetaMask (or any EIP-1193 wallet) and reload');
      return;
    }
    // fresh, cache-bypassing capacity check right before the irreversible payment
    const fresh = await getDiesAvailability(true);
    if (!fresh) {
      toast.error("Couldn't reach the provider to confirm capacity — try again in a moment");
      return;
    }
    setAvailability(fresh);
    const state = fresh[selected]?.state;
    if (state === 'SOLD_OUT') {
      toast.error(`${die.name} just sold out on the provider — pick another core`);
      return;
    }
    if (state === 'UNKNOWN') {
      toast.error("Couldn't confirm capacity with the provider — try again shortly (no payment sent)");
      return;
    }
    try {
      setPaying(true);
      const rental = await payWithWallet({ die, hours, totalEth, quote });
      setReceipt(rental);
      if (rental.status === 'PAYMENT_PENDING') {
        toast.success(`Payment sent — confirming ${die.name} on-chain…`);
      } else {
        toast.success(`Rented ${die.name} for ${hours}h — order ${rental.order_id.slice(0, 8)}`);
      }
    } catch (err) {
      devWarn('[nanovram] payment flow stopped:', err?.message);
      toast.error(err?.message?.slice(0, 90) || 'The wallet declined the job');
    } finally {
      setPaying(false);
    }
  };

  return (
    <main className="bg-ground-band" style={{ borderTop: 'none' }}>
      <section className="nv-section" style={{ paddingTop: 50 }}>
        <div className="nv-section-inner">
          <div style={{ textAlign: 'center', marginBottom: 44 }}>
            <h1 className="font-pixel" style={{ fontSize: 'clamp(26px, 5vw, 48px)', color: 'var(--nv-gold)', textShadow: '3px 3px 0 var(--nv-ink)', letterSpacing: 3, margin: '10px 0 16px' }} data-testid="rent-title">
              RENT A NANOVRAM
            </h1>
            <p style={{ color: 'var(--nv-cream)', fontSize: 13, letterSpacing: 2, textTransform: 'uppercase' }}>
              four cores, priced by the hour — the dollar legs breathe with the ledger
            </p>
          </div>

          <div className="nv-die-grid" data-testid="die-grid">
            {DIES.map((d) => (
              <DieCard key={d.id} d={d} selected={selected === d.id} quote={quote} onSelect={setSelected} avail={availability ? availability[d.id] : null} />
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 26, marginTop: 30 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
              <JobConfig die={die} hours={hours} setHours={setHours} totalEth={totalEth} quote={quote} paying={paying} soldOut={soldOut} onPay={pay} />
              {receipt && <ReceiptWindow receipt={receipt} />}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
              <SpecSheet quote={quote} />
              <ReadMe />
            </div>
          </div>

          <div style={{ marginTop: 30, maxWidth: 640 }}>
            <MyRentals />
          </div>
        </div>
      </section>
    </main>
  );
};

export default Rent;
